/**
 * Admin proof lifecycle — API client helpers (PL-PROOFLOOP-001/004/007).
 *
 * Wires the admin-facing generic proof endpoints (read + release) and the
 * revised-artwork direct-to-S3 multipart upload flow to typed TS
 * functions. All helpers:
 *  1. Call the shared axios instance.
 *  2. Unwrap the platform envelope via `unwrap`/`throwAsApiError`.
 *  3. Return camelCase domain shapes.
 *
 * The artwork-version upload flow mirrors `@dfq/shared/api/artworkUploader`
 * (the customer equivalent) but registers via
 * `POST /admin/artwork/{artworkFile}/versions` instead of
 * `POST /account/artwork`, and is gated by `prepress.artwork.version.create`
 * scope rather than `auth:storefront`.
 *
 * SH-PROOFLOOP-001.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  ArtworkFileVersionDTO,
  ArtworkVersionUploadCompleteDTO,
  ArtworkVersionUploadInitiateDTO,
  CreateArtworkFileVersionPayload,
  CreateDesignDraftArtworkPayload,
  OrderItemArtworkAttachmentDTO,
  ProofDTO,
  ProofReleaseDTO,
} from '@dfq/shared/types/proof';

/** Envelope `data` wrapper for the GET proof endpoint. */
interface AdminProofEnvelopeData {
  proof: ProofDTO;
}

/** Envelope `data` wrapper for the release endpoint. */
interface ProofReleaseEnvelopeData {
  order: ProofReleaseDTO;
}

/**
 * GET `/api/v1/admin/orders/{orderId}/proof`
 *
 * Returns the latest-round proof for the order with `proofS3Key` minted
 * into a presigned URL at read time, for the console prepress panel.
 *
 * Requires `auth:console` + `prepress.proof.view` scope. Throws `ApiError`
 * with `status: 404` for a missing order or a missing proof.
 */
export async function fetchAdminProof(orderId: number): Promise<ProofDTO> {
  try {
    const response = await apiClient.get<ApiEnvelope<AdminProofEnvelopeData>>(
      `/admin/orders/${orderId}/proof`,
    );
    const data = unwrap(response.data, response.status);
    return data.proof;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/admin/orders/{orderId}/prepress/proof/release`
 *
 * Transitions an `in_prepress` order to `awaiting_customer_approval` once
 * the order's latest proof round is `sent`.
 *
 * Requires `auth:console` + `prepress.proof.release` scope. Throws
 * `ApiError` with `status: 409` when the order is not `in_prepress` or the
 * latest proof is not yet `sent`.
 */
export async function releaseProof(orderId: number): Promise<ProofReleaseDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<ProofReleaseEnvelopeData>>(
      `/admin/orders/${orderId}/prepress/proof/release`,
    );
    const data = unwrap(response.data, response.status);
    return data.order;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/admin/artwork/uploads/initiate-multipart`
 *
 * Initiates a direct-to-S3 multipart upload session for a revised artwork
 * file and mints one presigned PUT URL per 5 MiB chunk.
 *
 * Requires `auth:console` + `prepress.artwork.version.create` scope.
 * Throttled at 10 requests/minute/staff user.
 */
export async function initiateArtworkVersionUpload(params: {
  filename: string;
  fileSizeBytes: number;
  mimeType: string;
}): Promise<ArtworkVersionUploadInitiateDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<{ upload: ArtworkVersionUploadInitiateDTO }>>(
      '/admin/artwork/uploads/initiate-multipart',
      {
        filename: params.filename,
        file_size_bytes: params.fileSizeBytes,
        mime_type: params.mimeType,
      },
    );
    const data = unwrap(response.data, response.status);
    return data.upload;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/admin/artwork/uploads/complete-multipart`
 *
 * Completes a previously initiated revised-artwork multipart upload —
 * reads the uploaded parts back from S3 via ListParts, then calls S3's
 * CompleteMultipartUpload API.
 *
 * Requires `auth:console` + `prepress.artwork.version.create` scope.
 * Throttled at 20 requests/minute/staff user.
 */
export async function completeArtworkVersionUpload(params: {
  uploadId: string;
  s3Key: string;
}): Promise<ArtworkVersionUploadCompleteDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<{ upload: ArtworkVersionUploadCompleteDTO }>>(
      '/admin/artwork/uploads/complete-multipart',
      {
        upload_id: params.uploadId,
        s3_key: params.s3Key,
      },
    );
    const data = unwrap(response.data, response.status);
    return data.upload;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/admin/artwork/{artworkFile}/versions`
 *
 * Registers an `artwork_file_versions` row pointing at an object already
 * direct-uploaded to S3 via `initiateArtworkVersionUpload` +
 * `completeArtworkVersionUpload`. `versionNumber` is computed server-side;
 * `uploadedByUserId` is always the authenticated staff caller.
 *
 * Requires `auth:console` + `prepress.artwork.version.create` scope.
 * Throws `ApiError` with `status: 422` when the S3 `HeadObject` check
 * fails (missing object or mismatched `s3_key` staff-id prefix).
 */
export async function createArtworkFileVersion(
  artworkFileId: number,
  payload: CreateArtworkFileVersionPayload,
): Promise<ArtworkFileVersionDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<{ version: ArtworkFileVersionDTO }>>(
      `/admin/artwork/${artworkFileId}/versions`,
      {
        s3_key: payload.s3Key,
        s3_version_id: payload.s3VersionId ?? null,
        file_size_bytes: payload.fileSizeBytes,
        notes: payload.notes ?? null,
      },
    );
    const data = unwrap(response.data, response.status);
    return data.version;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/admin/orders/{orderId}/prepress/design-draft-artwork`
 *
 * Registers the FIRST draft artwork for a design-service order — an
 * `artwork_files` row pointing at an object already direct-uploaded to S3
 * via `initiateArtworkVersionUpload` + `completeArtworkVersionUpload`,
 * attached to the order's line item, and dispatches the initial proof job.
 *
 * Requires `auth:console` + `prepress.artwork.version.create` scope. Throws
 * `ApiError` with `status: 409` when the order already has a draft (use
 * {@link createArtworkFileVersion} against the existing artwork instead),
 * and `status: 422` with `errors.order_item_id` when the target line item
 * is ambiguous.
 */
export async function createDesignDraftArtwork(
  orderId: number,
  payload: CreateDesignDraftArtworkPayload,
): Promise<OrderItemArtworkAttachmentDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<{ attachment: OrderItemArtworkAttachmentDTO }>>(
      `/admin/orders/${orderId}/prepress/design-draft-artwork`,
      {
        original_filename: payload.originalFilename,
        s3_key: payload.s3Key,
        s3_version_id: payload.s3VersionId ?? null,
        file_size_bytes: payload.fileSizeBytes,
        mime_type: payload.mimeType,
        ...(payload.orderItemId != null ? { order_item_id: payload.orderItemId } : {}),
      },
    );
    const data = unwrap(response.data, response.status);
    return data.attachment;
  } catch (err) {
    throwAsApiError(err);
  }
}
